<template>
  <div class="ftp-servers-page">
    <Navigation/>
    <v-main>
      <v-container fluid class="ftp-servers">

    <div class="fs-header">
      <div>
        <h1 class="fs-title">
          <v-icon large color="#2d5a3d">dns</v-icon>
          Serveurs FTP
        </h1>
        <p class="fs-subtitle">
          Une source de données existe par elle-même. Plusieurs univers peuvent
          s'y abonner, et la donnée reste accessible même si un univers disparaît.
        </p>
      </div>

      <div class="fs-header-actions">
        <v-btn text color="#2d5a3d" :loading="loading" @click="load">
          <v-icon left>refresh</v-icon>
          Actualiser
        </v-btn>
        <v-btn color="#2d5a3d" dark @click="newServer">
          <v-icon left>add</v-icon>
          Nouveau serveur
        </v-btn>
      </div>
    </div>

    <v-row>
      <!-- Liste -->
      <v-col cols="12" md="5">
        <v-card class="fs-card" outlined>
          <v-card-title class="fs-card-title">
            <v-icon left color="#2d5a3d">storage</v-icon>
            Sources déclarées
            <v-spacer></v-spacer>
            <v-chip x-small outlined>{{ servers.length }}</v-chip>
          </v-card-title>

          <v-divider></v-divider>

          <div v-if="!servers.length && !loading" class="fs-empty pa-6">
            Aucune source déclarée. Tous les univers récupèrent encore leurs
            fichiers eux-mêmes, comme avant.
          </div>

          <v-list v-else dense class="fs-list">
            <template v-for="(server, i) in servers">
              <v-list-item
                  :key="server.id"
                  :class="{ 'fs-active': form.id === server.id }"
                  @click="edit(server)"
              >
                <v-list-item-content>
                  <v-list-item-title class="fs-server-name">
                    {{ server.name }}
                    <v-icon v-if="!server.active" small color="#b0b8b4" class="ml-1" title="Inactif">pause_circle</v-icon>
                    <v-icon v-if="server.last_error" small color="#d9534f" class="ml-1" :title="server.last_error">error_outline</v-icon>
                  </v-list-item-title>

                  <v-list-item-subtitle class="fs-location">{{ server.location }}</v-list-item-subtitle>

                  <div class="fs-badges">
                    <v-chip x-small outlined class="mr-1">Site {{ server.doc_site }}</v-chip>
                    <v-chip x-small outlined class="mr-1">
                      {{ server.site_ids.length }} univers
                    </v-chip>
                    <v-chip
                        v-if="server.start_date"
                        x-small
                        color="#2d5a3d"
                        text-color="white"
                        class="mr-1"
                    >
                      depuis {{ shortDate(server.start_date) }}
                    </v-chip>
                  </div>
                </v-list-item-content>
              </v-list-item>
              <v-divider v-if="i < servers.length - 1" :key="'d' + server.id"></v-divider>
            </template>
          </v-list>
        </v-card>
      </v-col>

      <!-- Formulaire -->
      <v-col cols="12" md="7">
        <v-card class="fs-card" outlined>
          <v-card-title class="fs-card-title">
            <v-icon left color="#2d5a3d">{{ form.id ? 'edit' : 'add_circle' }}</v-icon>
            {{ form.id ? 'Modifier la source' : 'Nouvelle source' }}
            <v-spacer></v-spacer>
            <v-btn v-if="form.id" small text @click="newServer">
              <v-icon left small>close</v-icon>
              Nouveau
            </v-btn>
          </v-card-title>

          <v-divider></v-divider>

          <v-card-text>
            <!-- Connexion -->
            <div class="fs-section">Connexion</div>

            <v-row dense>
              <v-col cols="12" md="6">
                <v-text-field v-model="form.name" label="Nom" outlined dense hide-details></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                    v-model="form.ftp_methods"
                    :items="[{text:'FTP classique', value:0},{text:'FTP sécurisé / SFTP', value:1}]"
                    label="Protocole"
                    outlined dense hide-details
                ></v-select>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="form.host" label="Hôte" placeholder="ftp.exemple.com" outlined dense hide-details></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="form.path" label="Chemin" placeholder="/data/" outlined dense hide-details></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="form.user" label="Utilisateur" outlined dense hide-details></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                    v-model="form.pwd"
                    label="Mot de passe"
                    type="password"
                    :placeholder="form.id ? 'Inchangé' : ''"
                    :hint="form.id ? 'Laisser vide pour conserver le mot de passe actuel' : ''"
                    persistent-hint
                    outlined dense
                ></v-text-field>
              </v-col>
            </v-row>

            <!-- Date de départ -->
            <div class="fs-section">
              Date de départ
              <v-btn x-small text color="#2d5a3d" :loading="probing" @click="probe">
                <v-icon left x-small>search</v-icon>
                Sonder la source
              </v-btn>
            </div>

            <p class="fs-hint">
              Les fichiers antérieurs à cette date ne sont jamais récupérés. Le tri
              a lieu avant toute autre vérification : ils ne sont ni pesés, ni
              téléchargés, ni enregistrés.
            </p>

            <!-- Résultat du sondage -->
            <v-alert v-if="caps && caps.error" dense text type="error" class="fs-alert">
              {{ caps.error }}
            </v-alert>

            <v-alert
                v-else-if="caps && !caps.can_filter"
                dense text type="warning"
                class="fs-alert"
            >
              Ce serveur ne fournit aucune date exploitable
              (listing <strong>{{ caps.listing }}</strong>, MDTM indisponible).
              Le filtre par date est donc désactivé : l'activer bloquerait tous les
              fichiers au lieu d'en écarter certains.
            </v-alert>

            <v-alert v-else-if="caps" dense text type="success" class="fs-alert">
              Listing <strong>{{ caps.listing }}</strong> —
              dates disponibles :
              <strong>{{ caps.available_basis.join(', ') }}</strong>
              <span v-if="caps.mdtm"> (via MDTM)</span>.
            </v-alert>

            <v-row dense>
              <v-col cols="12" md="5">
                <v-text-field
                    v-model="form.start_date"
                    label="À partir du"
                    type="datetime-local"
                    outlined dense hide-details
                    :disabled="!canFilter"
                    clearable
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-select
                    v-model="form.start_date_basis"
                    :items="basisItems"
                    label="Se baser sur"
                    outlined dense hide-details
                    :disabled="!canFilter"
                ></v-select>
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                    v-model.number="form.tz_offset_minutes"
                    label="Décalage (min)"
                    type="number"
                    outlined dense hide-details
                    hint="Si le serveur annonce de l'heure locale"
                ></v-text-field>
              </v-col>
            </v-row>

            <p class="fs-hint fs-basis-note" v-if="canFilter">
              <strong>Modification</strong> : un vieux fichier réécrit par le serveur
              repasse le filtre et entre avec tout son historique.
              <strong>Création</strong> : il reste écarté pour toujours.
            </p>

            <div class="fs-preview-bar" v-if="form.id">
              <v-btn small text color="#2d5a3d" :loading="previewing" @click="preview">
                <v-icon left small>visibility</v-icon>
                Voir ce qui serait écarté
              </v-btn>
              <span v-if="previewResult" class="fs-preview-summary">
                {{ previewResult.kept.length }} gardé(s) ·
                {{ previewResult.skipped_old.length }} trop ancien(s) ·
                {{ previewResult.skipped_undetermined.length }} sans date
              </span>
            </div>

            <div v-if="previewResult" class="fs-preview">
              <table class="fs-table">
                <thead>
                <tr><th>Fichier</th><th>Date</th><th>Source</th><th class="center">Statut</th></tr>
                </thead>
                <tbody>
                <tr v-for="f in previewResult.files" :key="f.name">
                  <td>{{ f.name }}</td>
                  <td class="fs-mono">{{ f.date || '—' }}</td>
                  <td>
                    <v-chip x-small outlined :color="f.precise ? '' : '#c9822a'">
                      {{ f.source }}{{ f.precise ? '' : ' (approx.)' }}
                    </v-chip>
                  </td>
                  <td class="center">
                    <v-icon small :color="statusColor(f)">{{ statusIcon(f) }}</v-icon>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>

            <!-- Parsing -->
            <div class="fs-section">Lecture des fichiers</div>

            <p class="fs-hint">
              La source porte sa propre configuration de lecture : il n'y a plus
              un univers unique à qui la demander.
            </p>

            <v-row dense>
              <v-col cols="12" md="4">
                <v-text-field v-model="form.type" label="Type" outlined dense hide-details></v-text-field>
              </v-col>
              <v-col cols="12" md="2">
                <v-text-field v-model="form.delimiter" label="Délimiteur" outlined dense hide-details></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-switch
                    v-model="form.active"
                    :true-value="1" :false-value="0"
                    label="Source active"
                    inset hide-details color="#2d5a3d"
                    class="mt-1"
                ></v-switch>
              </v-col>
              <v-col cols="12">
                <div class="fs-sub-block">
                  <DataMappingEditor v-model="form.data_mapping"/>
                </div>
              </v-col>
              <v-col cols="12">
                <div class="fs-sub-block">
                  <DateFormatsEditor v-model="form.date_formats"/>
                </div>
              </v-col>
            </v-row>

            <!-- Univers abonnés -->
            <div class="fs-section">Univers abonnés</div>

            <p class="fs-hint">
              Rattacher un univers lui donne accès à <strong>tout l'historique</strong> de
              cette source immédiatement : la donnée ne bouge pas, seule la
              résolution change.
            </p>

            <v-autocomplete
                v-model="form.site_ids"
                :items="siteItems"
                item-text="label"
                item-value="id"
                label="Univers"
                multiple chips small-chips deletable-chips
                outlined dense hide-details
            ></v-autocomplete>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions class="fs-actions">
            <v-btn v-if="form.id" text color="#d9534f" @click="confirmDelete">
              <v-icon left small>delete</v-icon>
              Supprimer
            </v-btn>
            <v-btn v-if="form.id" text :loading="testing" @click="test">
              <v-icon left small>network_check</v-icon>
              Tester
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn color="#2d5a3d" dark :disabled="!canSave" :loading="saving" @click="save">
              <v-icon left>save</v-icon>
              Enregistrer
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <!-- Suppression -->
    <v-dialog v-model="deleteDialog" max-width="560">
      <v-card>
        <v-card-title>Supprimer cette source ?</v-card-title>
        <v-card-text>
          <p>
            Les univers abonnés seront détachés. Par défaut, les données
            Elasticsearch sont <strong>conservées</strong> — mais plus rien ne
            pointera vers elles depuis l'application.
          </p>
          <v-checkbox
              v-model="purgeData"
              color="#d9534f"
              hide-details
              label="Supprimer aussi définitivement les données Elasticsearch"
          ></v-checkbox>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog = false">Annuler</v-btn>
          <v-btn color="#d9534f" dark :loading="deleting" @click="doDelete">Supprimer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
      </v-container>
    </v-main>
  </div>
</template>

<script>
import Navigation from "@/components/Navigation.vue";
import DataMappingEditor from "@/components/DataMappingEditor.vue";
import DateFormatsEditor from "@/components/DateFormatsEditor.vue";

export default {
  name: "FtpServers",

  components: {Navigation, DataMappingEditor, DateFormatsEditor},

  data() {
    return {
      loading: false,
      saving: false,
      testing: false,
      probing: false,
      previewing: false,
      deleting: false,

      servers: [],
      sites: [],

      form: this.emptyForm(),
      caps: null,
      previewResult: null,

      deleteDialog: false,
      purgeData: false,
    };
  },

  computed: {
    siteItems() {
      return this.sites.map((s) => ({
        id: s.id,
        label: `${s.name}${s.client_name ? ' — ' + s.client_name : ''}`,
      }));
    },

    /**
     * Le filtre n'est ouvert que si la source sait donner des dates.
     * Tant qu'on n'a pas sondé, on laisse la main (cas de l'édition).
     */
    canFilter() {
      if (!this.caps) return true;
      return !!this.caps.can_filter;
    },

    basisItems() {
      const available = (this.caps && this.caps.available_basis) || ["modify", "create"];

      return [
        {text: "Date de modification", value: "modify", disabled: !available.includes("modify")},
        {text: "Date de création", value: "create", disabled: !available.includes("create")},
      ];
    },

    canSave() {
      return this.form.name && this.form.host && this.form.user && this.form.path
          && (this.form.id || this.form.pwd);
    },
  },

  created() {
    this.load();
  },

  methods: {
    emptyForm() {
      return {
        id: null,
        name: "",
        host: "",
        user: "",
        pwd: "",
        path: "",
        ftp_methods: 0,
        type: "",
        delimiter: ",",
        data_mapping: "",
        date_formats: "",
        start_date: null,
        start_date_basis: "modify",
        tz_offset_minutes: 0,
        active: 1,
        site_ids: [],
      };
    },

    async load() {
      this.loading = true;

      try {
        const data = await this.$store.dispatch("ftpServersRequest");
        this.servers = data.servers || [];

        // Les univers, pour l'autocomplete.
        const sites = await this.$store.dispatch("sitesRequest", {per_page: 500}).catch(() => null);

        if (sites && sites.data) {
          this.sites = sites.data.map((s) => ({
            id: s.id,
            name: s.name,
            client_name: s.client ? s.client.name : null,
          }));
        }
      } catch (err) {
        this.showErr(err);
      } finally {
        this.loading = false;
      }
    },

    newServer() {
      this.form = this.emptyForm();
      this.caps = null;
      this.previewResult = null;
    },

    edit(server) {
      this.form = {
        id: server.id,
        name: server.name,
        host: server.host,
        user: server.user,
        pwd: "",
        path: server.path,
        ftp_methods: server.ftp_methods,
        type: server.type || "",
        delimiter: server.delimiter || ",",
        data_mapping: server.data_mapping || "",
        date_formats: server.date_formats || "",
        start_date: server.start_date ? server.start_date.substring(0, 16) : null,
        start_date_basis: server.start_date_basis || "modify",
        tz_offset_minutes: server.tz_offset_minutes || 0,
        active: server.active,
        site_ids: [...server.site_ids],
      };

      this.caps = server.listing_capabilities || null;
      this.previewResult = null;
    },

    async probe() {
      this.probing = true;

      try {
        const payload = this.form.id
            ? {id: this.form.id}
            : {
              host: this.form.host,
              user: this.form.user,
              pwd: this.form.pwd,
              path: this.form.path,
              ftp_methods: this.form.ftp_methods,
              tz_offset_minutes: this.form.tz_offset_minutes,
            };

        this.caps = await this.$store.dispatch("ftpServerProbeRequest", payload);

        // La base choisie n'est plus disponible : on retombe sur ce qui existe.
        const available = this.caps.available_basis || [];

        if (available.length && !available.includes(this.form.start_date_basis)) {
          this.form.start_date_basis = available[0];
        }

        if (!this.caps.can_filter) {
          this.form.start_date = null;
        }
      } catch (err) {
        this.showErr(err);
      } finally {
        this.probing = false;
      }
    },

    async preview() {
      this.previewing = true;

      try {
        this.previewResult = await this.$store.dispatch("ftpServerPreviewRequest", {
          id: this.form.id,
          start_date: this.form.start_date,
          start_date_basis: this.form.start_date_basis,
          tz_offset_minutes: this.form.tz_offset_minutes,
        });
      } catch (err) {
        this.showErr(err);
      } finally {
        this.previewing = false;
      }
    },

    statusIcon(f) {
      if (!f.date) return "help_outline";
      if (!this.previewResult.kept.includes(f.name)) return "block";
      return "check_circle";
    },

    statusColor(f) {
      if (!f.date) return "#c9822a";
      if (!this.previewResult.kept.includes(f.name)) return "#b0b8b4";
      return "#2d5a3d";
    },

    async test() {
      this.testing = true;

      try {
        const result = await this.$store.dispatch("ftpServerTestRequest", {id: this.form.id});

        this.$store.commit("setSnack", {
          type: result.success ? "green" : "red",
          err: result.success ? "Connexion réussie." : (result.message || "Connexion impossible."),
        });

        await this.load();
      } catch (err) {
        this.showErr(err);
      } finally {
        this.testing = false;
      }
    },

    async save() {
      this.saving = true;

      try {
        const payload = {...this.form};

        // Mot de passe vide en édition = on ne le touche pas.
        if (this.form.id && !payload.pwd) {
          delete payload.pwd;
        }

        if (!payload.start_date) {
          payload.start_date = null;
        }

        const server = await this.$store.dispatch("ftpServerSaveRequest", payload);

        this.$store.commit("setSnack", {type: "green", err: "Source enregistrée."});

        await this.load();
        this.edit(server);
      } catch (err) {
        this.showErr(err);
      } finally {
        this.saving = false;
      }
    },

    confirmDelete() {
      this.purgeData = false;
      this.deleteDialog = true;
    },

    async doDelete() {
      this.deleting = true;

      try {
        await this.$store.dispatch("ftpServerDeleteRequest", {
          id: this.form.id,
          purge: this.purgeData,
        });

        this.$store.commit("setSnack", {type: "green", err: "Source supprimée."});

        this.deleteDialog = false;
        this.newServer();
        await this.load();
      } catch (err) {
        this.showErr(err);
      } finally {
        this.deleting = false;
      }
    },

    shortDate(iso) {
      return iso ? String(iso).substring(0, 10) : "";
    },

    showErr(err) {
      const data = err && err.response && err.response.data;
      const message = (data && data.message) || data || "Une erreur est survenue";

      this.$store.commit("setSnack", {
        type: "red",
        err: typeof message === "string" ? message : JSON.stringify(message),
      });
    },
  },
};
</script>

<style scoped>
.ftp-servers {
  padding: 24px;
  max-width: 1600px;
}

.fs-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.fs-header-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.fs-title {
  font-size: 1.6rem;
  font-weight: 600;
  color: #1f2d24;
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
}

.fs-subtitle {
  color: #6b7a70;
  margin: 6px 0 0;
  max-width: 720px;
  font-size: 0.9rem;
}

.fs-card {
  border-radius: 10px;
  overflow: hidden;
}

.fs-card-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1f2d24;
}

.fs-list {
  max-height: 640px;
  overflow-y: auto;
}

.fs-active {
  background: #eef5f0;
  box-shadow: inset 3px 0 0 #2d5a3d;
}

.fs-server-name {
  font-weight: 600;
  font-size: 0.9rem;
}

.fs-location {
  font-family: monospace;
  font-size: 0.74rem;
}

.fs-badges {
  margin-top: 5px;
}

.fs-empty {
  color: #8a9a90;
  font-size: 0.88rem;
  font-style: italic;
}

.fs-section {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #2d5a3d;
  margin: 22px 0 8px;
  padding-bottom: 5px;
  border-bottom: 1px solid #e6ebe8;
}

.fs-section:first-child {
  margin-top: 0;
}

.fs-hint {
  font-size: 0.78rem;
  color: #8a9a90;
  margin: 0 0 12px;
}

.fs-basis-note {
  margin-top: 10px;
}

.fs-alert {
  font-size: 0.8rem;
}

.fs-preview-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 10px;
  flex-wrap: wrap;
}

.fs-preview-summary {
  font-size: 0.78rem;
  color: #6b7a70;
}

.fs-preview {
  max-height: 260px;
  overflow-y: auto;
  border: 1px solid #e6ebe8;
  border-radius: 8px;
  margin-top: 8px;
}

.fs-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.78rem;
}

.fs-table th {
  text-align: left;
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #8a9a90;
  font-weight: 600;
  padding: 6px 10px;
  border-bottom: 1px solid #e6ebe8;
  background: #f6f8f7;
  position: sticky;
  top: 0;
}

.fs-table td {
  padding: 5px 10px;
  border-bottom: 1px solid #f4f6f5;
  color: #1f2d24;
}

.fs-table .center {
  text-align: center;
  width: 60px;
}

.fs-mono {
  font-family: monospace;
  font-size: 0.74rem;
  color: #6b7a70;
}

.fs-sub-block {
  border: 1px solid #e6ebe8;
  border-radius: 10px;
  padding: 14px 16px;
  background: #fbfcfb;
}

.fs-actions {
  padding: 12px 16px;
  gap: 8px;
}
</style>
